import { useState, ReactNode, memo, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import {
  GlobalModalContext,
  ModalConfig,
  ModalContentPropsBase,
} from '../Contexts/GlobalModalContext';

export const GlobalModalProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [modalKey, setModalKey] = useState<string | undefined>(undefined);

  const showModal = useCallback(
    (config: ModalConfig, key?: string) => {
      setModalConfig(() => config);
      setModalKey(key);
      setIsVisible(true);
    },
    []
  );

  const hideModal = useCallback(() => {
    setIsVisible(false);
    setModalConfig(null);
    setModalKey(undefined);
  }, []);

  const contextValue = useMemo(
    () => ({
      showModal,
      hideModal,
      isOpen: isVisible,
      modalKey
    }),
    [showModal, hideModal, isVisible, modalKey]
  );

  useEffect(() => hideModal(), [hideModal, location.key]);

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {children}
      <ModalPortal isVisible={isVisible} modalConfig={modalConfig} hideModal={hideModal} />
    </GlobalModalContext.Provider>
  );
};

const ModalPortal = memo(
  ({
    isVisible,
    modalConfig,
    hideModal,
  }: {
    isVisible: boolean;
    modalConfig: ModalConfig | null;
    hideModal: () => void;
  }) => {
    return createPortal(
      <div
        className={clsx(
          'fixed inset-0 flex items-center justify-center bg-black/40 z-50 transition-opacity duration-150 animate-fade-in',
          isVisible ? 'visible pointer-events-auto' : 'hidden pointer-events-none'
        )}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) hideModal();
        }}
      >
        {modalConfig && (
          <div
            className={`bg-theme-surface-2 border border-theme-border z-[51] rounded-xl p-6 w-full shadow-xl transition duration-150 transform max-h-[calc(100vh-4rem)] overflow-y-auto ${modalConfig.tailwindMaxWidthClass || 'max-w-lg'} ${modalConfig.isMaxHeight ? 'h-full' : ''}`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              {typeof modalConfig.content === 'function'
                ? <modalConfig.content close={hideModal} />
                : modalConfig.content}
            </div>
          </div>
        )}
      </div>,
      document.body
    );
  }
);